import React, { useEffect } from 'react';
import { Site } from '@99designs/frontend-components/apps/global-nav/lib/Site';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { trackEvent } from '@99designs/tracking';
import Box from '../../../shared/components/Box';
import Columns, { Column } from '../../../shared/components/Columns';
import ConfettiBackground from '../../../shared/components/ConfettiBackground';
import CtaPair from '../../../shared/components/CtaPair';
import Debug from '../../../shared/components/Debug';
import Heading from '../../../shared/components/Heading';
import { CTALink } from '../../../shared/components/Link';
import LinkGarden from '../../../shared/components/LinkGarden';
import ProgressBar from '../../../shared/components/ProgressBar';
import StickyBar from '../../../shared/components/StickyBar';
import { FullWidthPageSection, GridPageSection } from '../../../shared/components/VerticalSpacing';
import capitalize from '../../../shared/lib/capitalize';
import localizedLowercase from '../../../shared/lib/localizedLowercase';
import { categoryLaunchPath, categoryPath } from '../../../shared/lib/platformUrls';
import { colors } from '../../../shared/lib/styles';
import Stack from '../../../shared/spa/core-components/Stack';
import alternativeSites from '../../lib/alternativeSites';
import Head from '../Head';
import BlogSection from './BlogSection';
import BreadCrumbsSection from './BreadCrumbsSection';
import BriefSection from './BriefSection';
import CollaborationSection from './CollaborationSection';
import ContestCallout from './ContestCallout';
import { ContestType } from './ContestOverviewData';
import Designers from './Designers';
import HeroSection from './HeroSection';
import Inspiration from './Inspiration';
import RelatedContests from './RelatedContests';
import StatsSection from './StatsSection';
import WinnersSection from './WinnersSection';

const MINIMUN_RELATED_CONTESTS_TO_DISPLAY = 5;
const MIN_NUM_INSPIRING_DESIGNS = 3;

export interface Props {
    contest: ContestType;
    site: Site;
    currentPath: string;
}

export default function ContestOverviewLayout({ contest, site, currentPath }: Props) {
    const categoryKey = contest.category.key;

    useEffect(() => {
        trackEvent('Viewed Public Contest Overview', { category: categoryKey });
    }, [categoryKey]);

    const collaborationEntry = contest.entries[0];
    const galleryEntries = contest.entries.slice(1, 11);
    const renderStatsSection = galleryEntries.length > 0 || !contest.isCompleted;
    const designersWithDesigns = contest.designers.filter((d) => d.designs.length > 0);
    let robotsMetaTagContent = 'NOODP,NOYDIR';
    if (contest.isUnlisted || contest.isRobotsNoIndex) {
        robotsMetaTagContent = `${robotsMetaTagContent},noindex,nofollow`;
    }
    return (
        <>
            <Head
                title={formatMessage(__('{contestTitle} | {categoryTitle} contest'), {
                    contestTitle: capitalize(contest.title.toLowerCase()),
                    categoryTitle: contest.category.title,
                })}
                description={formatMessage(
                    __(
                        '{contestOwner} created a custom {categoryTitle} on 99designs. They got dozens of unique ideas from professional designers and picked their favorite.'
                    ),
                    {
                        contestOwner: contest.owner.displayName,
                        categoryTitle: contest.category.title.toLowerCase(),
                    }
                )}
                meta={
                    <>
                        <meta name="robots" content={robotsMetaTagContent} />
                        {alternativeSites.map(([locale, domain]) => (
                            <link
                                rel="alternate"
                                hrefLang={locale}
                                key={locale}
                                href={`https://${domain}${currentPath}`}
                            />
                        ))}
                    </>
                }
                site={site}
            />

            <Box paddingTop={['space6', 'space6', 'space6', 'space6']}>
                <GridPageSection verticalSpacing={'space12'} id="bread-crumbs">
                    <BreadCrumbsSection contest={contest} />
                </GridPageSection>
            </Box>

            <ConfettiBackground numSquares={55}>
                <GridPageSection id="hero">
                    <HeroSection contest={contest} />
                </GridPageSection>
            </ConfettiBackground>

            <FullWidthPageSection verticalSpacing={'space7'}>
                <StickyBar
                    hide
                    scrollProgressBar={(progress) => <ProgressBar progress={progress} />}
                >
                    <Box
                        style={{ width: '100%' }}
                        paddingTop="space6"
                        paddingBottom="space6"
                        textAlign="center"
                    >
                        <CtaPair
                            variations={['no-margin', 'chameleon', 'only-cta-mobile']}
                            primary={{
                                label: __('Get your own design'),
                                url: categoryLaunchPath(contest.category.key),
                                color: colors.heritage,
                                variations: ['jumbo', 'fluid', 'primary', 'chameleon'],
                            }}
                            secondary={
                                <CTALink
                                    href={categoryPath(contest.category.key)}
                                    target="_blank"
                                    color="heritage"
                                >
                                    {formatMessage(__('Learn more about {categoryTitle}'), {
                                        categoryTitle: localizedLowercase(
                                            site.siteConfig.locale,
                                            contest.category.title
                                        ),
                                    })}
                                </CTALink>
                            }
                        />
                    </Box>
                </StickyBar>
                <ContestCallout contest={contest} />
            </FullWidthPageSection>

            <Stack space={['space15', 'space16']}>
                <GridPageSection id="brief">
                    <BriefSection
                        category={contest.category}
                        chosenPackage={contest.package}
                        brief={contest.brief}
                        deliverableFileTypes={contest.deliverableFileTypes}
                    />
                </GridPageSection>

                {renderStatsSection && (
                    <FullWidthPageSection id="stats">
                        <StatsSection
                            designerCount={contest.designerCount}
                            designCount={contest.entryCount}
                            winnerCount={contest.winningEntryCount}
                            entries={galleryEntries}
                            category={contest.category}
                            isCompleted={contest.isCompleted}
                        />
                    </FullWidthPageSection>
                )}

                <GridPageSection id="collaboration">
                    <CollaborationSection
                        category={contest.category}
                        clientName={contest.owner.displayName}
                        featureEntry={collaborationEntry}
                    />
                </GridPageSection>

                <ConfettiBackground numSquares={16} fixedHeight={900}>
                    <FullWidthPageSection id="winners">
                        <WinnersSection contest={contest} />
                    </FullWidthPageSection>
                </ConfettiBackground>

                {designersWithDesigns.length > 0 && (
                    <FullWidthPageSection id="designers">
                        <Designers
                            designers={designersWithDesigns}
                            contestCategory={contest.category}
                        />
                    </FullWidthPageSection>
                )}
                {contest.relatedContests.length >= MINIMUN_RELATED_CONTESTS_TO_DISPLAY && (
                    <FullWidthPageSection id="contests">
                        <RelatedContests
                            contestCategory={contest.category}
                            relatedContests={contest.relatedContests}
                        />
                    </FullWidthPageSection>
                )}

                {contest.inspiringDesigns.length >= MIN_NUM_INSPIRING_DESIGNS && (
                    <FullWidthPageSection id="inspiration">
                        <Inspiration
                            category={contest.category}
                            industry={contest.industry}
                            inspiringDesigns={contest.inspiringDesigns}
                        />
                    </FullWidthPageSection>
                )}
                {contest.blogPosts.length > 0 && (
                    <GridPageSection id="reference">
                        {contest.inspirationTermLinks.length == 0 && (
                            <BlogSection blogPosts={contest.blogPosts} splitView={true} />
                        )}
                        {contest.inspirationTermLinks.length > 0 && (
                            <Columns>
                                <Column width={[12, 12, 6, 6]}>
                                    <Box paddingBottom={['space10', 'space10', 'none', 'none']}>
                                        <BlogSection blogPosts={contest.blogPosts} />
                                    </Box>
                                </Column>
                                <Column width={[12, 12, 6, 6]}>
                                    <Heading type="h3" variations={['h4']}>
                                        {__('More design ideas:')}
                                    </Heading>
                                    <LinkGarden
                                        data={contest.inspirationTermLinks}
                                        columns={[1, 2, 2, 2]}
                                    />
                                </Column>
                            </Columns>
                        )}
                    </GridPageSection>
                )}
            </Stack>
            <Box paddingBottom={['space15', 'space15', 'space16', 'space16']}></Box>

            <Debug debugMode={false /* TODO: Implement via debug flag, experiment cookie, etc */} />
        </>
    );
}
